/* eslint-disable jsx-a11y/media-has-caption */
import React from "react";
import { NavBar } from "../../common/NavBar";
import { AlertsTable } from "./AlertsTable";
import "video-react/dist/video-react.css";
import { AlertData, Order } from "./AlertsUtils/alertsInterfaces";
import { AlertsIdentifierBox } from "./AlertsIdentifierBox";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAlerts } from "./AlertsUtils/getAlerts";
import BackdropLoadingScreen from "../../common/BackdropLoadingScreen";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import useWindowDimensions from "../../common/useWindowDimensions";
import { NotesDialog } from "./NotesDialog";
import { Alert, Dialog, DialogContent, Paper } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import { handleForwardButtonClick } from "./AlertsUtils/handleForwardButtonClick";
import { handleBackButtonClick } from "./AlertsUtils/handleBackButtonClick";
import { hideMutedFromView } from "./AlertsUtils/isAlertMuted";
import { alertsDetailPaneScrollClassName } from "./AlertsUtils/alertsDetailPane";
import { AlertDetailHeader } from "./AlertDetailHeader";
import { AlertClipPlayer } from "./AlertClipPlayer";
import { AlertClipToolbar } from "./AlertClipToolbar";
import {
  AlertsTableDensity,
  ALERTS_NAV_AUTO_COLLAPSE_WINDOW_WIDTH,
  ALERTS_PAGE_MIN_WIDTH_PX,
  getAlertsGridTemplateColumns,
  getAlertsPageWidth,
  readPinnedAlertsTableDensity,
  resolveAlertsTableDensity,
  shouldShowAlertDetailPane,
  shouldShowAlertsApplyFiltersPrompt,
  writePinnedAlertsTableDensity,
} from "./AlertsUtils/alertsDensity";
import ErrorBoundary from "../../../ErrorBoundary";
import { Dispatcher } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { handleRowSelected } from "./AlertsUtils/handleRowSelected";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { ActiveIgnoreZonesWindow } from "./ActiveIgnoreZonesWindow";
import CameraInfoTable from "./CameraInfoTable";
import { saveAs } from "file-saver";
import { updateSearchParamValues } from "../../utils/updateSearchParamValues";
import { getParentAccounts } from "../../utils/getParentAccounts";
import { FiltersAlerts } from "./FiltersAlerts";
import { convertHHMMTime } from "./AlertsUtils/convertHHMMTime";
import { handleCreateFilterTypeName } from "./AlertsUtils/handleCreateFilterTypeName";
import { getIgnoreZoneDialogInfo } from "./AlertsUtils/getIgnoreZoneDialogInfo";
import { getLineCrossingsDialogInfo } from "./AlertsUtils/getLineCrossingsDialogInfo";
import { alertDetectionIncludesLineCrossing } from "./AlertsUtils/alertTypeStyles";
import { ActiveLineCrossingsWindow } from "./ActiveLineCrossingsWindow";
import { checkForRedirectAndFetchMP4 } from "./AlertsUtils/checkForRedirectAndFetchMP4";
import { getSiteByParentGroupsPlusAssignSiteDDValueArray } from "./AlertsUtils/getSiteByParentGroupsPlusAssignSiteDDValueArray";
import { getCamerasBySitesPlusAssignCameraDDValueArray } from "./AlertsUtils/getCamerasBySitesPlusAssignCameraDDValueArray";
import { useAlertsStore } from "./AlertsStore/alertsStore";
import { ViewSiteProtocolDialog } from "./ViewSiteProtocolDialog";
import { MuteAlertsDialog } from "./MuteAlertsDialog";
import {
  formatTimestampWithTimezone,
  SeeMutedSiteAndCamerasDialog,
} from "./SeeMutedAlertsDialog";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { useAppStore } from "../../../appStore";
import { getUserTimezone } from "../../utils/getUserTimezone";
import { checkSelectedRowForMuting } from "./AlertsUtils/checkSelectedRowForMuting";
import { shouldRefetchVideoForMismatch } from "./AlertsUtils/videoMismatchGuard";
import {
  clearVideoUrlCache,
  getCachedVideoUrl,
  isTempVideoUrl,
  cacheVideoUrl,
  getVideoUrlCacheKey,
  generateVideoRequestId,
  isFetchInProgressForRow,
  isVideoRequestValid,
  invalidateAllPendingRequests,
} from "./AlertsUtils/videoUrlCache";
import { createMonitoringAPICall } from "../../utils/createMonitoringAPICall";
import { AxiosResponse } from "axios";
import {
  resolveSelectionOutsideVlmFilter,
  getVlmStatusMessage,
  hasAlertVlmClassification,
  hasDoubleTakeInAlerts,
  VlmFilterType,
} from "./AlertsUtils/vlmFilters";
import { useResetVlmFilterOnNewFilters } from "./AlertsUtils/useResetVlmFilterOnNewFilters";
import {
  arrowStateForNavigableIndex,
  filterAlertsBeforeVlm,
  getNavigableAlerts,
  setNavigableAlertsContext,
} from "./AlertsUtils/getNavigableAlerts";
import { isLiveStreamingAvailableForAlert } from "./AlertsUtils/liveStreamingAvailability";
import { getVlmVerdictColor } from "./VlmVerdictIndicator";
import {
  AlertViewModeTabs,
  AlertViewMode,
  StreamProtocol,
} from "./AlertViewModeTabs";
import { LiveStreamPlayer } from "./LiveStreamPlayer";
import { SiteVideoWall } from "./SiteVideoWall/SiteVideoWall";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  SPLIT_VIEW_DEFAULT_HEIGHT,
  SPLIT_VIEW_DEFAULT_WIDTH,
  SPLIT_VIEW_MIN_WIDTH,
  VideoHeightResizeHandle,
  getSplitPaneWidth,
  getVideoPanelArrowTopPx,
} from "./AlertsUtils/splitViewResize";

/** Side slots between the alert-pane edge and the clip (ENG-2070). */
const ALERT_NAV_ARROW_GUTTER_PX = 26;
/** ChevronLeft/Right are optically centered; keep the button icon-sized only. */
const ALERT_NAV_ARROW_ICON_SX = { fontSize: 40 };
const ALERT_NAV_ARROW_BUTTON_SX = { minWidth: 0, padding: 0, lineHeight: 1 };

declare module "@mui/material/styles" {
  interface Palette {
    neutral: Palette["primary"];
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#FF8400",
    },
  },
});

const parseNumberList = (str: string): number[] => str.split(",").map(Number);

const Alerts = ({
  navBarCollapsed,
  setNavBarCollapsed,
}: {
  navBarCollapsed: boolean;
  setNavBarCollapsed: Dispatcher<boolean>;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { flags } = useFeatureFlag();
  const { userPermissions } = useAppStore();
  const viewAlertMuting = userPermissions.alert_muting;
  const showLineCrossings = flags?.showLineCrossings;
  const vlmFiltersFlag = Boolean(flags.showVlmFilters);
  // User-level gate. Whether a given alert can actually stream is decided per
  // selected row below (liveStreamingAvailable).
  const showLiveStreaming =
    Boolean(flags.showLiveStreaming) && userPermissions.live_streaming;
  const [viewMode, setViewMode] = React.useState<AlertViewMode>("clip");
  const [streamProtocol, setStreamProtocol] =
    React.useState<StreamProtocol>("hls");
  const [siteWallMiniOpen, setSiteWallMiniOpen] = React.useState(false);
  const [detailPaneWidth, setDetailPaneWidth] = React.useState(0);
  const [videoPanelHeight, setVideoPanelHeight] = React.useState<number | null>(
    null
  );
  const detailPaneRef = React.useRef<HTMLDivElement | null>(null);
  const [pinnedDensity, setPinnedDensity] =
    React.useState<AlertsTableDensity | null>(() =>
      readPinnedAlertsTableDensity()
    );
  const [showIgnoreZoneOverlay, setShowIgnoreZoneOverlay] =
    React.useState(false);
  const [showLineCrossingOverlay, setShowLineCrossingOverlay] =
    React.useState(false);

  const {
    setSiteId,
    setViewSiteProtocolDialogOpen,
    siteNotes,
    setSiteNotes,
    getSiteNotes,
    cameraNotes,
    getCameraNotes,
    siteIntegrationTypes,
    setMutedAlertsDialogOpen,
    mutedAlertsDialogOpen,
    mutedAlertSites,
    mutedAlertCameras,
    seeMutedSiteAndCamerasDialogOpen,
    resetAlertsStore,
    fetchSharedMutes,
    alertState,
    setAlertState,
    mutedAlertInfo,
    muteAlertsError,
    setMuteAlertsError,
    selectedRowIsMuted,
    setSelectedRowIsMuted,
  } = useAlertsStore();

  React.useEffect(() => {
    resetAlertsStore();
    clearVideoUrlCache(); // Clear video URL cache on page visit

    // Cleanup function - invalidate pending requests when component unmounts
    // This prevents state updates on unmounted components
    return () => {
      invalidateAllPendingRequests();
    };
  }, []);

  const group = searchParams.get("group");
  const site = searchParams.get("site");
  const camera = searchParams.get("camera");
  const start_date = searchParams.get("start_date");
  const end_date = searchParams.get("end_date");
  const start_time = searchParams.get("start_time");
  const end_time = searchParams.get("end_time");
  const filter_type = searchParams.get("filter_type");
  const detection_type = searchParams.get("detection_type");
  const has_video = searchParams.get("has_video");

  const [userFilters, setUserFilters] = React.useState<any>({
    parent_group: group,
    site: site ? parseNumberList(site) : [],
    camera: camera ? parseNumberList(camera) : [],
    start_date: start_date ? new Date(start_date) : null,
    end_date: end_date ? new Date(end_date) : null,
    start_time: start_time ? convertHHMMTime(start_time) : null,
    end_time: end_time ? convertHHMMTime(end_time) : null,
    filter_type: filter_type ? filter_type : "UNCLAIMED",
    has_video: has_video === "true",
  });

  React.useEffect(() => {
    if (Array.from(searchParams).length > 0) {
      localStorage.setItem("latestAlertFilters", JSON.stringify(userFilters));
      updateSearchParamValues(
        userFilters,
        searchParams,
        setSearchParams,
        detectionType
      );
    }
  }, []);

  const [alertData, setAlertData] = React.useState<AlertData[]>([]);

  const navigate = useNavigate();

  const [pageLoading, setPageLoading] = React.useState(false);

  const [selected, setSelected] = React.useState<string[]>([]);

  const [selectedRow, setSelectedRowState] = React.useState<any>(null);
  // The flag enables DoubleTake; the UI for it (queue tiles, verdict column,
  // banner) only renders when the alerts in view — the group/site/camera
  // filter's result — include a DoubleTake camera. Otherwise the tiles have
  // nothing to sort and just take up space.
  const showVlmFilters = React.useMemo(
    () => vlmFiltersFlag && hasDoubleTakeInAlerts(alertData),
    [vlmFiltersFlag, alertData]
  );
  const selectedRowRef = React.useRef<AlertData | null>(null);
  const videoMetaRef = React.useRef<{
    cameraPk: number | null;
    rowKey: string | null;
  } | null>(null);

  const getCurrentSelectedRow = () => selectedRowRef.current;
  const syncSelectedRowRef = (row: AlertData | null) => {
    selectedRowRef.current = row ?? null;
  };
  const setSelectedRow = React.useCallback((value: any) => {
    if (typeof value === "function") {
      setSelectedRowState((prev: any) => {
        const nextValue = value(prev);
        selectedRowRef.current = nextValue ?? null;
        return nextValue;
      });
      return;
    }
    selectedRowRef.current = value ?? null;
    setSelectedRowState(value);
  }, []);
  const getCurrentSelectedRowKey = () => {
    const row = selectedRowRef.current;
    if (!row) {
      return null;
    }
    const key = getVideoUrlCacheKey(row);
    return key === "|" ? null : key;
  };
  const setMP4URLWithMeta = (
    value: React.SetStateAction<string>,
    sourceRow?: AlertData | null
  ) => {
    const nextUrl = typeof value === "function" ? value(mP4URL) : value;
    setMP4URL(value);
    if (!nextUrl) {
      videoMetaRef.current = null;
      return;
    }
    const row = sourceRow ?? selectedRowRef.current;
    videoMetaRef.current = {
      cameraPk: row?.camera_pk ?? null,
      rowKey: row ? getVideoUrlCacheKey(row) : null,
    };
  };

  const [page, setPage] = React.useState(0);
  // Which selection AlertsTable last synced `page` to. Lives here, beside
  // `page`, because the table is unmounted during every background refresh.
  const alertsTablePageSyncedKeyRef = React.useRef<string | null>(null);

  const [selectdButtonsFilled, setSelectedButtonsFilled] = React.useState({
    true_threat: true,
    true_non_threat: true,
    false_positive: true,
    escalated_true_threat: true,
  });

  const userTimezone = getUserTimezone();

  React.useEffect(() => {
    selectedRowRef.current = selectedRow ?? null;
  }, [selectedRow]);

  React.useEffect(() => {
    const initializeData = async () => {
      // if(
      //   end_date && !isCurrentDate(end_date)
      // ) {
      //   setRowsSortable(true);
      // }

      getParentAccounts(
        setParentAccountsLoading,
        setParentAccounts,
        setParentIds,
        setParentNames,
        navigate
      );

      await fetchSharedMutes(navigate);

      if (group) {
        setLoadingSites(true);
        getSiteByParentGroupsPlusAssignSiteDDValueArray(
          group,
          navigate,
          setSiteOptions,
          setLoadingSites,
          site ? parseNumberList(site) : [],
          setSiteDropDownValue
        );
      }
      if (site) {
        getCamerasBySitesPlusAssignCameraDDValueArray(
          [site],
          navigate,
          setCameraOptions,
          setLoadingCameras,
          setCameraDropDownValue,
          camera ? parseNumberList(camera) : []
        );
      }
    };

    initializeData();
  }, []);

  // Refresh the shared (fleet-wide) muted lists every 30 seconds (ENG-2103)
  React.useEffect(() => {
    const interval = setInterval(() => {
      fetchSharedMutes(navigate);
    }, 30000); // 30 seconds

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, [navigate]);

  const [refreshingToken, setRefreshingToken] = React.useState(false);

  const [buttonClicked, setButtonClicked] = React.useState(false);

  const [fetchingFilteredData, setFetchingFilteredData] = React.useState(false);

  const [seeMoreURL, setSeeMoreURL] = React.useState<any>("");

  const [mP4URL, setMP4URL] = React.useState<any>("");

  const [thumbnailVideos, setThumbnailVideos] = React.useState<any>({});

  const [dateOutsideRange, setDateOutsideRange] = React.useState(false);

  const [ignoreZoneDialogOpen, setIgnoreZoneDialogOpen] = React.useState(false);

  const [siteOptions, setSiteOptions] = React.useState<any[]>([]);

  const [cameraOptions, setCameraOptions] = React.useState<any[]>([]);

  const [notesDialogOpen, setNotesDialogOpen] = React.useState(false);

  const [noteText, setNoteText] = React.useState("");

  const [videoClipLoading, setVideoClipLoading] = React.useState(true);

  const [alertsToDisplay, setAlertsToDisplay] = React.useState(false);

  const [refreshingData, setRefreshingData] = React.useState(false);

  const useAsyncGeneration = !userFilters?.has_video;

  const { width } = useWindowDimensions();

  // Auto-collapse the nav once when the window is (or becomes) narrower than
  // the threshold. Only the crossing collapses it, so the user can re-expand
  // it afterwards; with `navBarCollapsed` in the deps, their expand click was
  // reverted on the next render (ENG-1663).
  const previousWidthRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    const previousWidth = previousWidthRef.current;
    previousWidthRef.current = width;
    if (width <= 0) {
      return;
    }
    const crossedBelowThreshold =
      previousWidth === null ||
      previousWidth <= 0 ||
      previousWidth >= ALERTS_NAV_AUTO_COLLAPSE_WINDOW_WIDTH;
    if (width < ALERTS_NAV_AUTO_COLLAPSE_WINDOW_WIDTH && crossedBelowThreshold) {
      setNavBarCollapsed(true);
    }
  }, [width, setNavBarCollapsed]);

  const [alertIndex, setAlertIndex] = React.useState(0);

  const [timeFiltersDisabled, setTimeFiltersDisabled] = React.useState(true);

  const [loadingSites, setLoadingSites] = React.useState(group ? true : false);

  const [loadingCameras, setLoadingCameras] = React.useState(
    site ? true : false
  );

  const [selectedAlertCameraInfo, setSelectedAlertCameraInfo] = React.useState(
    {}
  );

  const [ignoreZonesSelectedAlert, setIgnoreZonesSelectedAlert] =
    React.useState([]);

  const [alertsIndexOrder, setAlertsIndexOrder] = React.useState([]);

  const [refreshInterval, setRefreshInterval] = React.useState(
    JSON.parse(localStorage.refresh_interval)
  );

  const [currentName, setCurrentName] = React.useState(
    filter_type ? handleCreateFilterTypeName(filter_type) : "Unresolved Alerts"
  );

  const [pageLoadedNumber, setPageLoadedNumber] = React.useState(0);

  const [parentAccounts, setParentAccounts] = React.useState([]);

  const [parentAccountsLoading, setParentAccountsLoading] =
    React.useState(true);

  const [parentIds, setParentIds] = React.useState(new Map());

  const [parentNames, setParentNames] = React.useState(new Map());

  const [warningIssued, setWarningIssued] = React.useState(false);

  const [alertLabels, setAlertLabels] = React.useState<string[]>(["All"]);

  const [detectionType, setDetectionType] = React.useState(
    detection_type ? detection_type : "All"
  );

  const [outcomeFilterType, setOutcomeFilterType] = React.useState("All");
  // Default to the working queue: everything except the likely false
  // positives (ENG-1756). Ignored entirely when showVlmFilters is off.
  const [vlmFilterType, setVlmFilterType] =
    React.useState<VlmFilterType>("Filtered Queue");
  const [tableSort, setTableSort] = React.useState<{
    order: Order;
    orderBy: string;
  }>({ order: "asc", orderBy: "" });
  const handleTableSortChange = React.useCallback(
    (order: Order, orderBy: string) => {
      setTableSort((prev) =>
        prev.order === order && prev.orderBy === orderBy
          ? prev
          : { order, orderBy }
      );
    },
    []
  );

  React.useEffect(() => {
    if (showVlmFilters) {
      setVlmFilterType("Filtered Queue");
    }
  }, [showVlmFilters]);
  useResetVlmFilterOnNewFilters({
    fetchingFilteredData,
    detectionType,
    outcomeFilterType,
    setVlmFilterType,
  });
  const vlmSelectionSyncRef = React.useRef<{
    selectionKey: string | null;
    vlmFilterType: VlmFilterType;
    showVlmFilters: boolean;
  }>({ selectionKey: null, vlmFilterType: "All", showVlmFilters: false });

  const [periodHours, setPeriodHours] = React.useState(2);

  const [queryLimitHit, setQueryLimitHit] = React.useState(false);

  const [filtersApplied, setFiltersApplied] = React.useState(false);

  const [loadingIgnoreZonesInfo, setLoadingIgnoreZonesInfo] =
    React.useState(false);

  const [lineCrossingsDialogOpen, setLineCrossingsDialogOpen] =
    React.useState(false);

  const [lineCrossingsSelectedAlert, setLineCrossingsSelectedAlert] =
    React.useState<any[]>([]);

  const [loadingLineCrossingsInfo, setLoadingLineCrossingsInfo] =
    React.useState(false);


  const [forwardArrowDisabled, setForwardArrowDisabled] = React.useState(false);

  const [backArrowDisabled, setBackArrowDisabled] = React.useState(true);

  const [showRefreshButton, setShowRefreshButton] = React.useState(false);

  const [, setVideoProcessing] = React.useState(false);

  const [partialVideoStatus, setPartialVideoStatus] = React.useState<
    "partial" | "checking" | "loading" | null
  >(null);

  const [rowsSortable, setRowsSortable] = React.useState(true);

  const [alertsRefreshing, setAlertsRefreshing] = React.useState(false);

  // Detect temp/partial video URLs and set status accordingly
  React.useEffect(() => {
    if (mP4URL && isTempVideoUrl(mP4URL)) {
      setPartialVideoStatus("partial");
    } else {
      setPartialVideoStatus(null);
    }
  }, [mP4URL]);

  // SAFEGUARD: Validate that the current video URL matches the selected row.
  // If there's a mismatch (e.g., due to a race condition), clear the video URL.
  // This ensures we never display a video from a different alert than the one selected.
  React.useEffect(() => {
    if (selectedRow) {
      const selectedRowKey = getVideoUrlCacheKey(selectedRow);
      const currentSelectedRowKey = getCurrentSelectedRowKey();

      // If the selectedRow state and ref don't match, there might be a sync issue.
      // Clear the video URL to be safe and trigger a re-fetch.
      if (currentSelectedRowKey && selectedRowKey !== currentSelectedRowKey) {
        setMP4URLWithMeta("");
        setVideoClipLoading(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow]);

  React.useEffect(() => {
    if (!selectedRow) {
      return;
    }

    const selectedRowKey = getVideoUrlCacheKey(selectedRow);
    const shouldRefetch = shouldRefetchVideoForMismatch({
      selectedCameraPk: selectedRow.camera_pk,
      videoCameraPk: videoMetaRef.current?.cameraPk,
      selectedRowKey,
      videoRowKey: videoMetaRef.current?.rowKey,
      hasVideoUrl: Boolean(mP4URL),
      isLoading: videoClipLoading,
    });

    const shouldRecoverStuckLoading =
      !mP4URL &&
      videoClipLoading &&
      !isFetchInProgressForRow(selectedRowKey) &&
      !getCachedVideoUrl(selectedRowKey);

    if (shouldRefetch || shouldRecoverStuckLoading) {
      setMP4URLWithMeta("");
      setVideoClipLoading(true);
      checkForRedirectAndFetchMP4(
        selectedRow,
        navigate,
        setMP4URLWithMeta,
        setVideoClipLoading,
        setShowRefreshButton,
        true,
        getCurrentSelectedRowKey,
        setVideoProcessing,
        useAsyncGeneration
      );
    }
  }, [selectedRow, mP4URL, videoClipLoading, navigate, setShowRefreshButton]);

  React.useLayoutEffect(() => {
    if (!selectedRow || !mP4URL || videoClipLoading) {
      return;
    }

    const selectedRowKey = getVideoUrlCacheKey(selectedRow);
    const videoRowKey = videoMetaRef.current?.rowKey;
    if (!videoRowKey || selectedRowKey === videoRowKey) {
      return;
    }

    setMP4URLWithMeta("");
    setVideoClipLoading(true);
    checkForRedirectAndFetchMP4(
      selectedRow,
      navigate,
      setMP4URLWithMeta,
      setVideoClipLoading,
      setShowRefreshButton,
      true,
      getCurrentSelectedRowKey,
      setVideoProcessing,
      useAsyncGeneration
    );
  }, [selectedRow, mP4URL, videoClipLoading, navigate, setShowRefreshButton, useAsyncGeneration]);

  // Handler to check for updated video when user clicks the button
  const handleCheckForUpdatedVideo = () => {
    if (!selectedRow) return;

    setPartialVideoStatus("checking");
    const AxiosUI = createMonitoringAPICall(``);
    const cacheKey = getVideoUrlCacheKey(selectedRow);

    // Generate a new request ID to track this specific request
    const requestId = generateVideoRequestId();
    // Capture the selected row's key at the time of the request
    const selectedRowKey = getVideoUrlCacheKey(selectedRow);

    // Poll function to check for updated video
    const pollForUpdatedVideo = (pollUrl: string, retriesRemaining: number) => {
      if (!isVideoRequestValid(requestId)) {
        return; // Request is stale
      }

      setTimeout(() => {
        AxiosUI.get(pollUrl).then(
          (response: AxiosResponse) => {
            if (!isVideoRequestValid(requestId)) {
              return;
            }
            if (getCurrentSelectedRowKey() !== selectedRowKey) {
              return;
            }

            if (typeof response.data === "string") {
              // Got the new video URL
              cacheVideoUrl(cacheKey, response.data);
              setMP4URLWithMeta(response.data);
              setPartialVideoStatus("loading");
              setTimeout(() => {
                setPartialVideoStatus((prev) => (prev === "loading" ? null : prev));
              }, 2000);
              return;
            }

            // Still processing — retry if we have retries left
            if (response.data?.status === "processing" && retriesRemaining > 1) {
              pollForUpdatedVideo(pollUrl, retriesRemaining - 1);
              return;
            }
            setPartialVideoStatus("partial");
          },
          (error) => {
            if (!isVideoRequestValid(requestId)) {
              return;
            }
            const status = error.response?.status;
            if (status === 404 && retriesRemaining > 1) {
              // Video not ready yet, keep polling
              pollForUpdatedVideo(pollUrl, retriesRemaining - 1);
              return;
            }
            // Retries exhausted or other error
            setPartialVideoStatus("partial");
          }
        );
      }, 500);
    };

    AxiosUI.get(
      `alert/clip/?approx_capture_timestamp=${selectedRow.approx_capture_timestamp}&window_id=${selectedRow.window_id}&async_generation=true&force_video=true`
    ).then(
      (response: AxiosResponse) => {
        // CRITICAL: Validate that this request is still valid and the selected row hasn't changed.
        // This prevents showing video from a previously selected alert.
        if (!isVideoRequestValid(requestId)) {
          // Request is stale - user has selected a different row. Ignore this response.
          return;
        }

        // Additional validation: check if the current selected row matches
        if (getCurrentSelectedRowKey() !== selectedRowKey) {
          // Selected row has changed - ignore this response
          return;
        }

        const responseData = response.data;
        if (responseData && typeof responseData === "object" && responseData.status === "processing") {
          // Got 202 - start polling for the new video
          const pollBaseUrl = `alert/video/?approx_capture_timestamp=${selectedRow.approx_capture_timestamp}&window_id=${selectedRow.window_id}&poll=true`;
          const pollUrl = responseData.poll_url
            ? responseData.poll_url.replace(/^\/?monitoring-api\//, "")
            : pollBaseUrl;
          pollForUpdatedVideo(pollUrl, 15); // Poll up to 15 times (7.5 seconds)
          return;
        }

        if (typeof responseData === "string") {
          // Got URL directly - update video and cache
          cacheVideoUrl(cacheKey, responseData);
          setMP4URLWithMeta(responseData);
          setPartialVideoStatus("loading");
          setTimeout(() => {
            setPartialVideoStatus((prev) => (prev === "loading" ? null : prev));
          }, 2000);
        } else {
          // Unexpected response
          setPartialVideoStatus("partial");
        }
      },
      () => {
        // On error, only update status if request is still valid
        if (isVideoRequestValid(requestId)) {
          setPartialVideoStatus("partial");
        }
      }
    );
  };

  const refreshText = (
    <>
      <RefreshIcon fontSize="small" />
      &nbsp;Try Refresh
    </>
  );

  // Step to the previous / next alert in the filtered list. Shared by the
  // arrow-key shortcuts and the orange arrows beside the clip. Do not flip
  // refreshingData here: under React 17 a native listener's update is not
  // batched with the helper's own setRefreshingData(false), and the table
  // would unmount/remount (page + scroll reset) on every step.
  const goToPreviousAlert = () => {
    handleBackButtonClick(
      selectedRow,
      setAlertIndex,
      setSelectedRow,
      alertData,
      navigate,
      setMP4URLWithMeta,
      setVideoClipLoading,
      setSelectedButtonsFilled,
      setSeeMoreURL,
      setAlertData,
      setRefreshingData,
      setSelectedAlertCameraInfo,
      setIgnoreZonesSelectedAlert,
      alertsIndexOrder,
      detectionType,
      outcomeFilterType,
      setBackArrowDisabled,
      setForwardArrowDisabled,
      setShowRefreshButton,
      hideMutedFromView(userFilters.filter_type)
        ? mutedAlertSites
        : [],
      hideMutedFromView(userFilters.filter_type)
        ? mutedAlertCameras
        : [],
      getCurrentSelectedRowKey,
      setVideoProcessing,
      useAsyncGeneration,
      syncSelectedRowRef,
      showVlmFilters ? vlmFilterType : "All",
      userFilters.filter_type
    );
  };

  const goToNextAlert = () => {
    handleForwardButtonClick(
      selectedRow,
      setAlertIndex,
      setSelectedRow,
      alertData,
      navigate,
      setMP4URLWithMeta,
      setVideoClipLoading,
      setSelectedButtonsFilled,
      setSeeMoreURL,
      setAlertData,
      setRefreshingData,
      setSelectedAlertCameraInfo,
      setIgnoreZonesSelectedAlert,
      alertsIndexOrder,
      detectionType,
      outcomeFilterType,
      setForwardArrowDisabled,
      setBackArrowDisabled,
      setShowRefreshButton,
      hideMutedFromView(userFilters.filter_type)
        ? mutedAlertSites
        : [],
      hideMutedFromView(userFilters.filter_type)
        ? mutedAlertCameras
        : [],
      getCurrentSelectedRowKey,
      setVideoProcessing,
      useAsyncGeneration,
      syncSelectedRowRef,
      showVlmFilters ? vlmFilterType : "All",
      userFilters.filter_type
    );
  };

  // Keyboard shortcuts handler
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts when video is loaded and not in a dialog
      if (
        !mP4URL ||
        videoClipLoading ||
        ignoreZoneDialogOpen ||
        notesDialogOpen
      ) {
        return;
      }

      switch (event.key) {
        case "ArrowLeft":
          if (!backArrowDisabled) {
            event.preventDefault();
            goToPreviousAlert();
          }
          break;
        case "ArrowRight":
          if (!forwardArrowDisabled) {
            event.preventDefault();
            goToNextAlert();
          }
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    mP4URL,
    videoClipLoading,
    ignoreZoneDialogOpen,
    notesDialogOpen,
    backArrowDisabled,
    forwardArrowDisabled,
    selectedRow,
    alertData,
    navigate,
    alertsIndexOrder,
    detectionType,
    outcomeFilterType,
    showVlmFilters,
    vlmFilterType,
  ]);

  const parsedSiteList = site ? parseNumberList(site) : [];
  const parseSiteListNames: string[] = [];

  const cameraIds = new Map();
  cameraOptions.forEach((object: any) => {
    cameraIds.set(object.camera_name, object.id);
  });

  const siteIds = new Map();
  siteOptions.forEach((object: any) => {
    siteIds.set(object.name, object.id);
  });

  const siteNames = new Map();
  parsedSiteList.forEach((object: any) => {
    siteNames.set(object.id, object.name);
  });

  const [siteDropDownValue, setSiteDropDownValue] = React.useState<string[]>(
    []
  );

  const [cameraDropDownValue, setCameraDropDownValue] = React.useState<
    string[]
  >([]);

  const ref = React.useRef<HTMLDivElement>(null);

  const setScrollTop = (event: any) => {
    localStorage.setItem("aletsScrollTop", event.target.scrollTop);
  };

  React.useEffect(() => {
    if (ref) {
      if (ref.current !== null) {
        ref.current.scrollTop = localStorage.aletsScrollTop;
      }
    }
  }, [alertData]);

  const scrollToTop = () => {
    if (ref) {
      if (ref.current !== null) {
        ref.current.scrollTop = 0;
      }
    }
  };

  React.useEffect(() => {
    if (Array.from(searchParams).length > 0) {
      localStorage.setItem("pageLoadedNumber", "0");
      setPageLoadedNumber(0);
      setPageLoading(true);
      if (start_date && end_date) {
        if (start_date === end_date) {
          setTimeFiltersDisabled(false);
        } else {
          setTimeFiltersDisabled(true);
        }
      }
      getAlerts(
        setAlertData,
        navigate,
        setSelectedRow,
        setPageLoading,
        setSeeMoreURL,
        setMP4URLWithMeta,
        userFilters,
        setThumbnailVideos,
        setDateOutsideRange,
        setVideoClipLoading,
        setAlertsToDisplay,
        setSelectedAlertCameraInfo,
        setIgnoreZonesSelectedAlert,
        setAlertsIndexOrder,
        setRefreshInterval,
        setFetchingFilteredData,
        setCurrentName,
        setPage,
        setSelectedButtonsFilled,
        setUserFilters,
        cameraOptions,
        siteOptions,
        setRefreshingData,
        0,
        refreshInterval,
        setParentAccountsLoading,
        setParentAccounts,
        setParentIds,
        setParentNames,
        setRefreshingToken,
        refreshingToken,
        setAlertLabels,
        setPeriodHours,
        setFiltersApplied,
        setForwardArrowDisabled,
        setBackArrowDisabled,
        setShowRefreshButton,
        setRowsSortable,
        setAlertsRefreshing,
        getCurrentSelectedRowKey,
        mutedAlertSites,
        mutedAlertCameras,
        setVideoProcessing
      );
    }
  }, []);

  setNavigableAlertsContext({
    detectionType,
    outcomeFilterType,
    vlmFilterType,
    showVlmFilters,
    mutedAlertSites,
    mutedAlertCameras,
    filterType: userFilters?.filter_type,
    order: tableSort.order,
    orderBy: tableSort.orderBy,
  });

  React.useEffect(() => {
    const navigationArgs = {
      alertData,
      detectionType,
      outcomeFilterType,
      vlmFilterType,
      showVlmFilters,
      mutedAlertSites,
      mutedAlertCameras,
      filterType: userFilters?.filter_type,
      order: tableSort.order,
      orderBy: tableSort.orderBy,
    };
    const filteredBeforeVlm = filterAlertsBeforeVlm(navigationArgs);
    const filteredData = getNavigableAlerts(navigationArgs);

    // Use the ref to get the most current selected row value
    // This avoids stale closure issues when alertData updates trigger this effect
    const currentSelectedRow = getCurrentSelectedRow() ?? selectedRow;
    const selectionKey = currentSelectedRow
      ? `${currentSelectedRow.id ?? ""}|${
          currentSelectedRow.approx_capture_timestamp ?? ""
        }|${currentSelectedRow.window_id ?? ""}`
      : null;

    // Helper to find a row in filtered data by matching identifiers
    const findRowInFilteredData = (rowToFind: AlertData | null) => {
      if (!rowToFind) return null;
      return filteredData.find(
        (row: AlertData) =>
          (row?.id && rowToFind?.id && row.id === rowToFind.id) ||
          (row.approx_capture_timestamp === rowToFind.approx_capture_timestamp &&
            row.window_id === rowToFind.window_id)
      );
    };

    // If we have no current selection but filteredData has items, select the first one
    if (!currentSelectedRow && filteredData.length > 0) {
      handleRowSelected(
        filteredData[0],
        alertData,
        setAlertData,
        setSelectedButtonsFilled,
        setSeeMoreURL,
        navigate,
        setMP4URLWithMeta,
        setVideoClipLoading,
        setSelectedRow,
        setSelectedAlertCameraInfo,
        setIgnoreZonesSelectedAlert,
        refreshingToken,
        filteredData,
        setBackArrowDisabled,
        setForwardArrowDisabled,
        setShowRefreshButton,
        getCurrentSelectedRowKey,
        setVideoProcessing,
        useAsyncGeneration,
        syncSelectedRowRef
      );
      const firstArrows = arrowStateForNavigableIndex(0, filteredData.length);
      setBackArrowDisabled(firstArrows.backDisabled);
      setForwardArrowDisabled(firstArrows.forwardDisabled);
      return;
    }

    // Check if the current selected row is still in the filtered data
    if (currentSelectedRow) {
      const selectedRowInFilteredData = findRowInFilteredData(currentSelectedRow);

      if (!selectedRowInFilteredData) {
        // Current selection not found in new data - fall back to localStorage or first row
        const getStoredSelectedRow = () => {
          try {
            if (
              localStorage.selectedRowAlerts &&
              localStorage.selectedRowAlerts !== "undefined"
            ) {
              return JSON.parse(localStorage.selectedRowAlerts);
            }
          } catch (error) {
            return null;
          }
          return null;
        };

        const storedSelectedRow = getStoredSelectedRow();
        const storedSelectedRowMatch = findRowInFilteredData(storedSelectedRow);

        const hiddenByVlmFilter =
          showVlmFilters &&
          vlmFilterType !== "All" &&
          filteredBeforeVlm.some(
            (row: AlertData) =>
              (row?.id &&
                currentSelectedRow?.id &&
                row.id === currentSelectedRow.id) ||
              (row.approx_capture_timestamp ===
                currentSelectedRow.approx_capture_timestamp &&
                row.window_id === currentSelectedRow.window_id)
          );
        const outsideVlmFilterAction = hiddenByVlmFilter
          ? resolveSelectionOutsideVlmFilter({
              selectionChanged:
                vlmSelectionSyncRef.current.selectionKey !== selectionKey,
              filterChanged:
                vlmSelectionSyncRef.current.vlmFilterType !== vlmFilterType ||
                vlmSelectionSyncRef.current.showVlmFilters !== showVlmFilters,
              filteredCount: filteredData.length,
            })
          : "keep";

        if (outsideVlmFilterAction === "select-first") {
          // Nobody picked this alert on purpose (page load, flag flip, tile
          // click, programmatic selection): go to the first one in the queue
          // rather than show an alert the table does not contain (ENG-1756).
          handleRowSelected(
            filteredData[0],
            alertData,
            setAlertData,
            setSelectedButtonsFilled,
            setSeeMoreURL,
            navigate,
            setMP4URLWithMeta,
            setVideoClipLoading,
            setSelectedRow,
            setSelectedAlertCameraInfo,
            setIgnoreZonesSelectedAlert,
            refreshingToken,
            filteredData,
            setBackArrowDisabled,
            setForwardArrowDisabled,
            setShowRefreshButton,
            getCurrentSelectedRowKey,
            setVideoProcessing,
            useAsyncGeneration,
            syncSelectedRowRef
          );
          const firstArrows = arrowStateForNavigableIndex(
            0,
            filteredData.length
          );
          setBackArrowDisabled(firstArrows.backDisabled);
          setForwardArrowDisabled(firstArrows.forwardDisabled);
        } else {
          // Preserve the user's current selection instead of auto-jumping to
          // another row when refresh/filtering temporarily excludes it. When it
          // was a DoubleTake verdict that moved it out of the queue mid-review,
          // "next" still carries on into the queue (handleForwardButtonClick).
          setBackArrowDisabled(true);
          setForwardArrowDisabled(
            outsideVlmFilterAction !== "keep-and-allow-forward"
          );
        }
      } else {
        // Current selection IS in filtered data - just update arrow states
        const selectedRowIndex = filteredData.findIndex(
          (row: AlertData) =>
            (row?.id && currentSelectedRow?.id && row.id === currentSelectedRow.id) ||
            (row.approx_capture_timestamp === currentSelectedRow.approx_capture_timestamp &&
              row.window_id === currentSelectedRow.window_id)
        );

        const arrows = arrowStateForNavigableIndex(
          selectedRowIndex,
          filteredData.length
        );
        setBackArrowDisabled(arrows.backDisabled);
        setForwardArrowDisabled(arrows.forwardDisabled);
      }
    }

    // What this run saw, so the next one can tell "the data moved under the
    // operator" from "the selection or the DoubleTake filter changed".
    vlmSelectionSyncRef.current = {
      selectionKey,
      vlmFilterType,
      showVlmFilters,
    };
  }, [
    alertData,
    detectionType,
    outcomeFilterType,
    showVlmFilters,
    vlmFilterType,
    selectedRow,
    mutedAlertSites,
    mutedAlertCameras,
    userFilters?.filter_type,
    tableSort,
  ]);

  React.useEffect(() => {
    setSiteNotes("");
    if (selectedRow?.customer_pk) {
      getSiteNotes(selectedRow.customer_pk, navigate);
    }
    if (selectedRow?.camera_pk) {
      getCameraNotes(selectedRow.camera_pk, navigate);
    }
  }, [selectedRow]);

  React.useEffect(() => {
    checkSelectedRowForMuting(
      selectedRow,
      mutedAlertSites,
      mutedAlertCameras,
      setSelectedRowIsMuted
    );
  }, [selectedRow, mutedAlertSites, mutedAlertCameras, setSelectedRowIsMuted]);

  const selectedRowKeyForRender = selectedRow
    ? getVideoUrlCacheKey(selectedRow)
    : null;

  React.useEffect(() => {
    setShowIgnoreZoneOverlay(false);
    setShowLineCrossingOverlay(false);
    // The overlay toggle only refetches when this is empty; without the reset
    // the previous alert's camera lines were drawn over the new alert's clip
    // (ignore zones are already reset per row in handleRowSelected).
    setLineCrossingsSelectedAlert([]);
  }, [selectedRowKeyForRender]);

  const canShowLineCrossings =
    Boolean(showLineCrossings) &&
    alertDetectionIncludesLineCrossing(selectedRow?.alert_labels);

  React.useEffect(() => {
    if (!canShowLineCrossings) {
      setShowLineCrossingOverlay(false);
    }
  }, [canShowLineCrossings]);

  React.useLayoutEffect(() => {
    const node = detailPaneRef.current;
    if (!node) return undefined;
    const updateWidth = () => {
      setDetailPaneWidth(Math.floor(node.clientWidth));
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, [seeMoreURL, alertsToDisplay, selectedRowKeyForRender]);
  const hasStaleVideoForCurrentSelection =
    Boolean(mP4URL) &&
    Boolean(selectedRowKeyForRender) &&
    Boolean(videoMetaRef.current?.rowKey) &&
    selectedRowKeyForRender !== videoMetaRef.current?.rowKey;
  const showFetchingVideoOverlay =
    hasStaleVideoForCurrentSelection || (videoClipLoading && !mP4URL);
  const hasVlmClassificationForSelectedRow =
    !!selectedRow && hasAlertVlmClassification(selectedRow);

  const liveStreamCameraId =
    selectedRow?.camera_pk != null ? String(selectedRow.camera_pk) : null;
  // ENG-2135: undefined until the site's about/ has loaded (getSiteNotes);
  // the talk-down control stays hidden until then.
  const selectedSiteIntegrationType =
    selectedRow?.customer_pk != null
      ? siteIntegrationTypes[String(selectedRow.customer_pk)]
      : undefined;
  // Per-alert gate: hide the Live Stream / Split View tabs and the video wall
  // for alerts whose site has live streaming off — the mint would only 403.
  // When no alert in the current filter can stream, nothing live is offered.
  const liveStreamingAvailable =
    showLiveStreaming && isLiveStreamingAvailableForAlert(selectedRow);
  const isSplitView = liveStreamingAvailable && viewMode === "split";
  const showClipPanel =
    !liveStreamingAvailable || viewMode === "clip" || viewMode === "split";
  const showLivePanel =
    liveStreamingAvailable && (viewMode === "live" || viewMode === "split");
  const navBarOffset = navBarCollapsed ? 75 : 150;
  const pageWidth = getAlertsPageWidth(width);
  const contentAreaWidth = Math.max(0, pageWidth - navBarOffset - 32);
  const workspaceWidth = Math.max(0, pageWidth - navBarOffset);
  const tableDensity = resolveAlertsTableDensity({
    workspaceWidth,
    pinnedMode: pinnedDensity,
  });
  const tableFraction = tableDensity === "condensed" ? 7 / 20 : 11 / 24;
  const videoColumnWidth = contentAreaWidth * (1 - tableFraction);
  // Side slots for the orange prev/next arrows (ENG-2070). Equal gutters on
  // both sides of the clip; the arrows are flex-centered inside each slot.
  const availableVideoWidth = Math.max(
    SPLIT_VIEW_MIN_WIDTH + ALERT_NAV_ARROW_GUTTER_PX * 2,
    detailPaneWidth || Math.floor(videoColumnWidth - 24)
  );
  const arrowGutterWidth = ALERT_NAV_ARROW_GUTTER_PX;
  const videoContentWidth = Math.max(
    SPLIT_VIEW_MIN_WIDTH,
    availableVideoWidth - arrowGutterWidth * 2
  );
  const videoLayoutWidth = videoContentWidth + arrowGutterWidth * 2;
  const videoContentHeight =
    videoPanelHeight ??
    Math.round(
      (videoContentWidth * SPLIT_VIEW_DEFAULT_HEIGHT) / SPLIT_VIEW_DEFAULT_WIDTH
    );
  const videoPanelArrowTopPx = getVideoPanelArrowTopPx(videoContentHeight);
  const splitPaneWidth = isSplitView
    ? getSplitPaneWidth(videoContentWidth)
    : videoContentWidth;
  const clipPanelWidth = isSplitView ? splitPaneWidth : videoContentWidth;
  const livePanelWidth = isSplitView ? splitPaneWidth : videoContentWidth;
  const canDownloadVideo =
    Boolean(mP4URL) &&
    !videoClipLoading &&
    !hasStaleVideoForCurrentSelection &&
    partialVideoStatus !== "partial" &&
    partialVideoStatus !== "checking";
  const canShowSiteVideoWall =
    liveStreamingAvailable && Boolean(selectedRow?.customer_pk);
  const showAlertDetailPane = shouldShowAlertDetailPane({
    seeMoreURL,
    alertsToDisplay,
    selectedRow,
  });
  const showApplyFiltersPrompt = shouldShowAlertsApplyFiltersPrompt({
    pageLoading,
    filtersApplied,
  });
  const alertsGridTemplateColumns = getAlertsGridTemplateColumns(tableDensity);

  const handleDensityChange = (mode: AlertsTableDensity) => {
    writePinnedAlertsTableDensity(mode);
    setPinnedDensity(mode);
  };

  const handleDownloadVideo = () => {
    if (mP4URL) {
      saveAs(mP4URL, "video.mp4");
    }
  };

  const handleOpenIgnoreZones = () => {
    setLoadingIgnoreZonesInfo(true);
    getIgnoreZoneDialogInfo(
      selectedRow,
      setSelectedAlertCameraInfo,
      setIgnoreZonesSelectedAlert,
      setLoadingIgnoreZonesInfo,
      navigate
    );
    setIgnoreZoneDialogOpen(true);
  };

  const handleOpenLineCrossings = () => {
    setLoadingLineCrossingsInfo(true);
    getLineCrossingsDialogInfo(
      selectedRow,
      setSelectedAlertCameraInfo,
      setLineCrossingsSelectedAlert,
      setLoadingLineCrossingsInfo,
      navigate
    );
    setLineCrossingsDialogOpen(true);
  };

  const handleToggleIgnoreZoneOverlay = () => {
    const next = !showIgnoreZoneOverlay;
    setShowIgnoreZoneOverlay(next);
    if (next && selectedRow && (!ignoreZonesSelectedAlert || ignoreZonesSelectedAlert.length === 0)) {
      getIgnoreZoneDialogInfo(
        selectedRow,
        setSelectedAlertCameraInfo,
        setIgnoreZonesSelectedAlert,
        setLoadingIgnoreZonesInfo,
        navigate
      );
    }
  };

  const handleToggleLineCrossingOverlay = () => {
    const next = !showLineCrossingOverlay;
    setShowLineCrossingOverlay(next);
    if (next && selectedRow && (!lineCrossingsSelectedAlert || lineCrossingsSelectedAlert.length === 0)) {
      getLineCrossingsDialogInfo(
        selectedRow,
        setSelectedAlertCameraInfo,
        setLineCrossingsSelectedAlert,
        setLoadingLineCrossingsInfo,
        navigate
      );
    }
  };

  const alertsContentMarginClass = navBarCollapsed ? "ml-[75px]" : "ml-[150px]";

  return (
    <div>
      {selectedRow && (
        <NotesDialog
          notesDialogOpen={notesDialogOpen}
          setNotesDialogOpen={setNotesDialogOpen}
          selectedRow={selectedRow}
          noteText={noteText}
          setNoteText={setNoteText}
          alertData={alertData}
          setAlertData={setAlertData}
          setSelectedRow={setSelectedRow}
          navigate={navigate}
        />
      )}
      {mutedAlertsDialogOpen && viewAlertMuting && (
        <MuteAlertsDialog selectedRow={selectedRow} navigate={navigate} />
      )}
      {seeMutedSiteAndCamerasDialogOpen && viewAlertMuting && (
        <SeeMutedSiteAndCamerasDialog />
      )}
      <Dialog open={ignoreZoneDialogOpen}>
        <IconButton
          aria-label="close"
          onClick={() => {
            setIgnoreZoneDialogOpen(false);
          }}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            zIndex: 2,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <ActiveIgnoreZonesWindow
            selectedAlertCameraInfo={selectedAlertCameraInfo}
            ignoreZonesSelectedAlert={ignoreZonesSelectedAlert}
            loadingIgnoreZonesInfo={loadingIgnoreZonesInfo}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={lineCrossingsDialogOpen}>
        <IconButton
          aria-label="close"
          onClick={() => {
            setLineCrossingsDialogOpen(false);
          }}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            zIndex: 2,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <ActiveLineCrossingsWindow
            selectedAlertCameraInfo={selectedAlertCameraInfo}
            lineCrossingsSelectedAlert={lineCrossingsSelectedAlert}
            loadingLineCrossingsInfo={loadingLineCrossingsInfo}
          />
        </DialogContent>
      </Dialog>
      <ViewSiteProtocolDialog />
      <BackdropLoadingScreen openState={fetchingFilteredData} />
      <BackdropLoadingScreen openState={buttonClicked} />
      <BackdropLoadingScreen openState={pageLoading} />
      <div className="absolute inset-0 overflow-x-auto overflow-y-hidden bg-actuate-grey">
        <div
          className="relative flex h-full min-h-0 w-full flex-col font-mulish"
          style={{ minWidth: ALERTS_PAGE_MIN_WIDTH_PX }}
        >
          {showApplyFiltersPrompt && (
            <div
              className={`pointer-events-none absolute top-[200px] left-0 right-0 z-10 ${alertsContentMarginClass} pr-4`}
            >
              <p className="text-center font-mulish text-[22px] text-actuate-blue">
                Please Apply Filters to view alerts.
              </p>
            </div>
          )}
          {/* Page-level notices (mute confirmations, warnings) live inside
              the flex column so they push the filter bar down instead of
              rendering behind the absolutely positioned page (which also
              shoved the side nav down, leaving a blank strip top-left). */}
          <div className={`shrink-0 ${alertsContentMarginClass} pr-4`}>
            {muteAlertsError && (
              <Alert
                severity="error"
                onClose={() => {
                  setMuteAlertsError("");
                }}
              >
                {muteAlertsError}
              </Alert>
            )}
            {alertState.siteMutedSuccess && (
              <Alert
                severity="success"
                onClose={() => {
                  setAlertState({
                    cameraMutedSuccess: false,
                    siteMutedSuccess: false,
                  });
                }}
              >
                Alert delivery for site {mutedAlertInfo.siteName} muted for all
                operators until{" "}
                {formatTimestampWithTimezone(
                  mutedAlertInfo.alertEndTime,
                  userTimezone
                )}
                .
              </Alert>
            )}
            {alertState.cameraMutedSuccess && (
              <Alert
                severity="success"
                onClose={() => {
                  setAlertState({
                    cameraMutedSuccess: false,
                    siteMutedSuccess: false,
                  });
                }}
              >
                Alert delivery for camera {mutedAlertInfo.cameraName} from site{" "}
                {mutedAlertInfo.siteName} muted for all operators until{" "}
                {formatTimestampWithTimezone(
                  mutedAlertInfo.alertEndTime,
                  userTimezone
                )}
                .
              </Alert>
            )}
            {dateOutsideRange && (
              <Alert
                severity="warning"
                onClose={() => {
                  setDateOutsideRange(false);
                }}
              >
                Alerts with dates outside of selected range are displayed.
              </Alert>
            )}
            {queryLimitHit && (
              <Alert
                severity="warning"
                onClose={() => {
                  setQueryLimitHit(false);
                }}
              >
                <p className="font-mulish font-bold text-[16px]">
                  The selected parent group is too large. Only the 100 most recent
                  alerts are displayed. Please select a specific site to view the
                  accurate number of alerts.
                </p>
              </Alert>
            )}
          </div>
          <div className="w-full shrink-0 bg-white">
            <div className={alertsContentMarginClass}>
              <FiltersAlerts
                navBarCollapsed={navBarCollapsed}
                userFilters={userFilters}
                setUserFilters={setUserFilters}
                navigate={navigate}
                setSiteOptions={setSiteOptions}
                setSiteDropDownValue={setSiteDropDownValue}
                setCameraOptions={setCameraOptions}
                setCameraDropDownValue={setCameraDropDownValue}
                setLoadingSites={setLoadingSites}
                setRefreshInterval={setRefreshInterval}
                parentAccounts={parentAccounts}
                parentIds={parentIds}
                parentAccountsLoading={parentAccountsLoading}
                siteOptions={siteOptions}
                siteDropDownValue={siteDropDownValue}
                loadingSites={loadingSites}
                setLoadingCameras={setLoadingCameras}
                cameraOptions={cameraOptions}
                cameraDropDownValue={cameraDropDownValue}
                loadingCameras={loadingCameras}
                setFetchingFilteredData={setFetchingFilteredData}
                setAlertData={setAlertData}
                setSelectedRow={setSelectedRow}
                setSeeMoreURL={setSeeMoreURL}
                setMP4URL={setMP4URLWithMeta}
                setSelectedButtonsFilled={setSelectedButtonsFilled}
                setThumbnailVideos={setThumbnailVideos}
                setDateOutsideRange={setDateOutsideRange}
                setPage={setPage}
                setVideoClipLoading={setVideoClipLoading}
                setAlertsToDisplay={setAlertsToDisplay}
                setPageLoading={setPageLoading}
                setTimeFiltersDisabled={setTimeFiltersDisabled}
                warningIssued={warningIssued}
                setWarningIssued={setWarningIssued}
                timeFiltersDisabled={timeFiltersDisabled}
                setFiltersApplied={setFiltersApplied}
                pageLoadedNumber={pageLoadedNumber}
                setPageLoadedNumber={setPageLoadedNumber}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                detectionType={detectionType}
                setDetectionType={setDetectionType}
                setSelectedAlertCameraInfo={setSelectedAlertCameraInfo}
                setIgnoreZonesSelectedAlert={setIgnoreZonesSelectedAlert}
                setAlertsIndexOrder={setAlertsIndexOrder}
                setCurrentName={setCurrentName}
                setRefreshingData={setRefreshingData}
                setRefreshingToken={setRefreshingToken}
                refreshingToken={refreshingToken}
                setAlertLabels={setAlertLabels}
                setPeriodHours={setPeriodHours}
                setQueryLimitHit={setQueryLimitHit}
                refreshInterval={refreshInterval}
                setOutcomeFilterType={setOutcomeFilterType}
                setForwardArrowDisabled={setForwardArrowDisabled}
                setBackArrowDisabled={setBackArrowDisabled}
                setShowRefreshButton={setShowRefreshButton}
                setRowsSortable={setRowsSortable}
                setAlertsRefreshing={setAlertsRefreshing}
                getCurrentSelectedRowKey={getCurrentSelectedRowKey}
                setVideoProcessing={setVideoProcessing}
              />
            </div>
          </div>
          {/* <div>
              <svg width={width}>
                <line
                  x1="0"
                  y1="0"
                  x2="100%"
                  y2="0"
                  style={{ stroke: `#BDBDBD`, strokeWidth: 2 }}
                />
              </svg>
          </div> */}
          <div
            className={`relative min-h-0 w-auto max-w-full flex-1 ${alertsContentMarginClass} pr-4`}
          >
            {filtersApplied && (
          <div
            className="grid h-full min-h-0"
            style={{
              display: "grid",
              gridTemplateColumns: showAlertDetailPane
                ? alertsGridTemplateColumns
                : "1fr",
              columnGap: 8,
            }}
          >
            <div className="relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden pt-[10px] pb-2">
              {!pageLoading && (
                <>
                    {/* Background refreshes (refreshingData) no longer unmount
                        the table: the remount reset its scroll position and
                        flashed a spinner every time new alerts arrived, so the
                        alert being viewed jumped. Only a filter fetch remounts. */}
                    {!fetchingFilteredData && (
                      <div className="flex min-h-0 flex-1 flex-col">
                        <div className="min-h-0 flex-1">
                        <AlertsTable
                          fillAvailableHeight
                          selected={selected}
                          alertData={alertData}
                          setAlertData={setAlertData}
                          setSelected={setSelected}
                          selectedRow={selectedRow}
                          setSelectedRow={setSelectedRow}
                          setSelectedButtonsFilled={setSelectedButtonsFilled}
                          setSeeMoreURL={setSeeMoreURL}
                          navigate={navigate}
                          setMP4URL={setMP4URLWithMeta}
                          page={page}
                          setPage={setPage}
                          pageSyncedKeyRef={alertsTablePageSyncedKeyRef}
                          userFilters={userFilters}
                          setFetchingFilteredData={setFetchingFilteredData}
                          setUserFilters={setUserFilters}
                          setThumbnailVideos={setThumbnailVideos}
                          setDateOutsideRange={setDateOutsideRange}
                          setVideoClipLoading={setVideoClipLoading}
                          setAlertsToDisplay={setAlertsToDisplay}
                          setPageLoading={setPageLoading}
                          setSelectedAlertCameraInfo={
                            setSelectedAlertCameraInfo
                          }
                          setIgnoreZonesSelectedAlert={
                            setIgnoreZonesSelectedAlert
                          }
                          setAlertsIndexOrder={setAlertsIndexOrder}
                          setRefreshInterval={setRefreshInterval}
                          currentName={currentName}
                          setCurrentName={setCurrentName}
                          pageLoadedNumber={pageLoadedNumber}
                          setPageLoadedNumber={setPageLoadedNumber}
                          setRefreshingData={setRefreshingData}
                          cameraOptions={cameraOptions}
                          siteOptions={siteOptions}
                          scrollToTop={scrollToTop}
                          refreshingToken={refreshingToken}
                          setRefreshingToken={setRefreshingToken}
                          setAlertLabels={setAlertLabels}
                          alertLabels={alertLabels}
                          detectionType={detectionType}
                          setDetectionType={setDetectionType}
                          setPeriodHours={setPeriodHours}
                          setQueryLimitHit={setQueryLimitHit}
                          searchParams={searchParams}
                          setSearchParams={setSearchParams}
                          refreshInterval={refreshInterval}
                          outcomeFilterType={outcomeFilterType}
                          setOutcomeFilterType={setOutcomeFilterType}
                          setBackArrowDisabled={setBackArrowDisabled}
                          setForwardArrowDisabled={setForwardArrowDisabled}
                          setShowRefreshButton={setShowRefreshButton}
                          setRowsSortable={setRowsSortable}
                          rowsSortable={rowsSortable}
                          setAlertsRefreshing={setAlertsRefreshing}
                          alertsRefreshing={alertsRefreshing}
                          getCurrentSelectedRowKey={getCurrentSelectedRowKey}
                          setVideoProcessing={setVideoProcessing}
                          useAsyncGeneration={useAsyncGeneration}
                          setSelectedRowRef={syncSelectedRowRef}
                          vlmFilterType={vlmFilterType}
                          setVlmFilterType={setVlmFilterType}
                          tableDensity={tableDensity}
                          onDensityChange={handleDensityChange}
                          periodHours={periodHours}
                          filtersApplied={filtersApplied}
                          parentAccounts={parentAccounts}
                          onTableSortChange={handleTableSortChange}
                        />
                        </div>
                      </div>
                    )}
                </>
              )}
            </div>
            {showAlertDetailPane && (
            <div
              ref={ref}
              className={alertsDetailPaneScrollClassName}
              onScroll={setScrollTop}
            >
              {/* Fills the pane so the card ends level with the alerts
                  table beside it; the outcome box and info table share any
                  spare height. Longer content still scrolls. */}
              <Paper
                className="flex w-full flex-col font-mulish"
                sx={{ p: 1.5, minHeight: "100%" }}
              >
                <div
                  ref={detailPaneRef}
                  className="flex w-full min-w-0 grow flex-col"
                >
                  {seeMoreURL && alertsToDisplay && selectedRow && (
                    <div>
                      <div>
                        <AlertDetailHeader
                          siteName={
                            selectedRow.display_name ||
                            selectedRow.customer_name ||
                            ""
                          }
                          cameraName={selectedRow.camera_name || ""}
                          siteHref={
                            selectedRow.customer_pk
                              ? `/sites/aboutsite/${selectedRow.customer_pk}`
                              : undefined
                          }
                          cameraHref={
                            selectedRow.customer_pk && selectedRow.camera_pk
                              ? `/sites/${selectedRow.customer_pk}/editcamera/${selectedRow.camera_pk}`
                              : undefined
                          }
                          alertLabels={selectedRow.alert_labels}
                          siteTime={selectedRow.formatted_date || ""}
                          seeMoreURL={seeMoreURL}
                          canMute={Boolean(viewAlertMuting)}
                          isMuted={Boolean(selectedRowIsMuted)}
                          onMute={() => setMutedAlertsDialogOpen(true)}
                          onDownloadVideo={handleDownloadVideo}
                          canDownloadVideo={canDownloadVideo}
                          onSiteVideoWall={() => setSiteWallMiniOpen(true)}
                          canShowSiteVideoWall={canShowSiteVideoWall}
                          onSeeIgnoreZones={handleOpenIgnoreZones}
                          onSeeLineCrossings={handleOpenLineCrossings}
                          showLineCrossings={canShowLineCrossings}
                        />
                      </div>
                      <div className="mt-2">
                        <div
                          className="grid items-start"
                          style={{
                            width: videoLayoutWidth,
                            gridTemplateColumns: `${arrowGutterWidth}px ${videoContentWidth}px ${arrowGutterWidth}px`,
                          }}
                        >
                          <div
                            className="flex justify-center pr-[10px]"
                            style={{ paddingTop: videoPanelArrowTopPx }}
                            id="arrow_back"
                          >
                            <OrangeTextButton
                              onClick={goToPreviousAlert}
                              text={
                                <ChevronLeftIcon
                                  id="backButton"
                                  sx={ALERT_NAV_ARROW_ICON_SX}
                                />
                              }
                              disabled={backArrowDisabled}
                              sx={ALERT_NAV_ARROW_BUTTON_SX}
                            />
                          </div>
                          <div
                            className="relative min-w-0"
                            id="video"
                          >
                            {liveStreamingAvailable && (
                              <AlertViewModeTabs
                                viewMode={viewMode}
                                onViewModeChange={setViewMode}
                                streamProtocol={streamProtocol}
                                onStreamProtocolChange={setStreamProtocol}
                                showStreamProtocolSelector={
                                  userPermissions.super_user
                                }
                                width={videoContentWidth}
                                talkDownCameraId={liveStreamCameraId}
                                talkDownCameraName={
                                  selectedRow?.camera_name || ""
                                }
                                talkDownIntegrationTypeName={
                                  selectedSiteIntegrationType
                                }
                                talkDownCapable={
                                  selectedRow?.talk_down_capable
                                }
                                talkDownParentGroupId={
                                  userFilters.parent_group
                                }
                                talkDownSiteId={selectedRow?.customer_pk}
                                talkDownSiteName={
                                  selectedRow?.display_name ||
                                  selectedRow?.customer_name ||
                                  ""
                                }
                              />
                            )}
                            <div className="w-full" style={{ width: videoContentWidth }}>
                              <div
                                className="relative w-full"
                                style={{ width: videoContentWidth }}
                              >
                                <div
                                  className="relative mb-4"
                                  style={{
                                    width: videoContentWidth,
                                    height: videoContentHeight,
                                  }}
                                >
                                  <div className="pointer-events-none absolute bottom-0 left-1/2 z-20 -translate-x-1/2 translate-y-1/2">
                                    <div className="pointer-events-auto">
                                      <VideoHeightResizeHandle
                                        height={videoContentHeight}
                                        onHeightChange={setVideoPanelHeight}
                                      />
                                    </div>
                                  </div>
                                <div
                                  className={
                                    isSplitView ? "grid grid-cols-2 gap-[2px]" : ""
                                  }
                                  style={{
                                    width: videoContentWidth,
                                    height: videoContentHeight,
                                  }}
                                >
                                  {showClipPanel && (
                                    <div
                                      className="relative"
                                      style={{ width: clipPanelWidth }}
                                    >
                                      <div
                                        style={{
                                          width: clipPanelWidth,
                                          height: videoContentHeight,
                                        }}
                                      >
                                        {showVlmFilters && hasVlmClassificationForSelectedRow && (
                                          <div className="absolute right-[10px] top-[10px] z-10">
                                            <div className="rounded-md border border-white/25 bg-black/65 px-[10px] py-[5px] font-mulish text-[11px] font-bold uppercase tracking-[0.08em] shadow-sm backdrop-blur-[1px]">
                                              <span
                                                style={{
                                                  color:
                                                    getVlmVerdictColor(
                                                      selectedRow
                                                    ),
                                                }}
                                              >
                                                DOUBLETAKE
                                              </span>
                                            </div>
                                          </div>
                                        )}
                                        {showFetchingVideoOverlay ? (
                                          <div
                                            className="flex flex-col items-center justify-center bg-black"
                                            style={{
                                              width: clipPanelWidth,
                                              height: videoContentHeight,
                                            }}
                                          >
                                            <ThemeProvider theme={theme}>
                                              <CircularProgress color="primary" />
                                            </ThemeProvider>
                                            <p className="font-mulish text-[20px] text-white mt-4">
                                              Fetching Video...
                                            </p>
                                          </div>
                                        ) : !mP4URL ? (
                                          <div
                                            className="flex flex-col items-center justify-center bg-black"
                                            style={{
                                              width: clipPanelWidth,
                                              height: videoContentHeight,
                                            }}
                                          >
                                            <p className="font-mulish text-[20px] text-white text-center">
                                              Video not available yet.
                                            </p>
                                            <p className="font-mulish text-[20px] text-white text-center">
                                              Use Try Refresh to check again.
                                            </p>
                                            <div className="mt-4">
                                              <OrangeTextButton
                                                onClick={() => {
                                                  handleRowSelected(
                                                    getCurrentSelectedRow(),
                                                    alertData,
                                                    setAlertData,
                                                    setSelectedButtonsFilled,
                                                    setSeeMoreURL,
                                                    navigate,
                                                    setMP4URLWithMeta,
                                                    setVideoClipLoading,
                                                    setSelectedRow,
                                                    setSelectedAlertCameraInfo,
                                                    setIgnoreZonesSelectedAlert,
                                                    refreshingToken,
                                                    alertData,
                                                    setBackArrowDisabled,
                                                    setForwardArrowDisabled,
                                                    setShowRefreshButton,
                                                    getCurrentSelectedRowKey,
                                                    setVideoProcessing,
                                                    useAsyncGeneration,
                                                    syncSelectedRowRef
                                                  );
                                                }}
                                                text={refreshText}
                                                disabled={false}
                                              />
                                            </div>
                                          </div>
                                        ) : (
                                          <div className={isSplitView ? "pb-[10px]" : ""}>
                                            <AlertClipPlayer
                                              src={mP4URL}
                                              width={clipPanelWidth}
                                              height={videoContentHeight}
                                              videoKey={`${getVideoUrlCacheKey(
                                                selectedRow
                                              )}-${mP4URL}`}
                                              approxCaptureTimestamp={
                                                selectedRow.approx_capture_timestamp
                                              }
                                              ignoreZones={ignoreZonesSelectedAlert || []}
                                              lineCrossings={lineCrossingsSelectedAlert || []}
                                              showIgnoreZones={showIgnoreZoneOverlay}
                                              showLineCrossings={showLineCrossingOverlay}
                                              canDownload={canDownloadVideo}
                                              onDownload={handleDownloadVideo}
                                            />
                                            {/* {showRefreshButton && (
                                <div className="pl-[240px] flex">
                                  <OrangeTextButton
                                    onClick={() => {
                                      setVideoClipLoading(true);
                                      checkForRedirectAndFetchMP4(
                                        selectedRow,
                                        navigate,
                                        setMP4URLWithMeta,
                                        setVideoClipLoading,
                                        setShowRefreshButton,
                                        false,
                                        getCurrentSelectedRowKey,
                                        setVideoProcessing,
                                        useAsyncGeneration
                                      );
                                    }}
                                    text={
                                      <>
                                        <RefreshIcon fontSize="small" />
                                        &nbsp;Refresh Video
                                      </>
                                    }
                                    disabled={false}
                                  />
                                  <Tooltip
                                    title={
                                      "If this button is visible the full clip may not have been ready. Click to refresh and check for full clip."
                                    }
                                    placement="right"
                                    sx={{
                                      fontSize: "15px",
                                      fontFamily: "mulish",
                                    }}
                                  >
                                    <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                                  </Tooltip>
                                </div>
                              )} */}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {showLivePanel && (
                                    <LiveStreamPlayer
                                      cameraId={liveStreamCameraId}
                                      enabled={showLivePanel}
                                      protocol={streamProtocol}
                                      width={livePanelWidth}
                                      height={videoContentHeight}
                                      liveBadgeTopClass={
                                        viewMode === "live" ? "top-8" : "top-3"
                                      }
                                    />
                                  )}
                                </div>
                                </div>
                                {liveStreamingAvailable && selectedRow?.customer_pk && (
                                  <SiteVideoWall
                                    siteId={selectedRow.customer_pk}
                                    siteName={
                                      selectedRow.display_name ??
                                      selectedRow.customer_name ??
                                      ""
                                    }
                                    alertContext={{
                                      detectionType:
                                        selectedRow.detection_type ??
                                        selectedRow.alert_labels?.[0] ??
                                        "Alert",
                                      cameraName: selectedRow.camera_name ?? "",
                                      formattedDate:
                                        selectedRow.formatted_date ?? "",
                                    }}
                                    defaultFocusedCameraId={liveStreamCameraId}
                                    miniOpen={siteWallMiniOpen}
                                    onMiniOpenChange={setSiteWallMiniOpen}
                                  />
                                )}
                              </div>
                              {showClipPanel &&
                                mP4URL &&
                                !videoClipLoading &&
                                !hasStaleVideoForCurrentSelection &&
                                partialVideoStatus !== null && (
                                  // Only mounted when there is a message, so
                                  // stepping between alerts doesn't add and
                                  // remove an empty strip below the clip.
                                  <div
                                    className="relative clear-both mt-1 space-y-1 font-mulish"
                                    style={{ width: videoContentWidth }}
                                  >
                                    {partialVideoStatus === "partial" && (
                                      <div className="flex flex-wrap items-center justify-center gap-x-1 px-1 py-2 text-center text-[14px] leading-snug text-gray-600">
                                        <span>Partial video displayed,</span>
                                        <OrangeTextButton
                                          onClick={handleCheckForUpdatedVideo}
                                          text="CLICK HERE"
                                          disabled={false}
                                        />
                                        <span>to check for updated video</span>
                                      </div>
                                    )}
                                    {partialVideoStatus === "checking" && (
                                      <div className="flex items-center justify-center py-2">
                                        <CircularProgress
                                          size={16}
                                          color="primary"
                                        />
                                        <p className="ml-2 text-[14px] text-gray-600">
                                          Checking for updated video...
                                        </p>
                                      </div>
                                    )}
                                    {partialVideoStatus === "loading" && (
                                      <div className="flex items-center justify-center py-2">
                                        <p className="text-[14px] text-green-600">
                                          Loading updated video
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              {showVlmFilters && showClipPanel && (
                                <div
                                  className="relative clear-both mt-2 rounded-md border px-[12px] py-[8px] font-mulish"
                                  data-testid="vlm-status-banner"
                                  style={{
                                    width: videoContentWidth,
                                    // Same colour as the tile, chip and column
                                    // for this alert (hex alpha: 66 = 40%,
                                    // 1A = 10%). Red is only ever Likely FP.
                                    borderColor: `${getVlmVerdictColor(
                                      selectedRow
                                    )}66`,
                                    backgroundColor: `${getVlmVerdictColor(
                                      selectedRow
                                    )}1A`,
                                  }}
                                >
                                  <p
                                    className="text-[12px] font-semibold"
                                    style={{
                                      color: getVlmVerdictColor(selectedRow),
                                    }}
                                  >
                                    {getVlmStatusMessage(selectedRow)}
                                  </p>
                                </div>
                              )}
                            </div>
                          {showClipPanel && (
                            <div
                              className="relative mx-auto"
                              style={{ width: videoContentWidth }}
                            >
                              <AlertClipToolbar
                                showIgnoreZones={showIgnoreZoneOverlay}
                                showLineCrossings={showLineCrossingOverlay}
                                onToggleIgnoreZones={handleToggleIgnoreZoneOverlay}
                                onToggleLineCrossings={
                                  handleToggleLineCrossingOverlay
                                }
                                canShowLineCrossings={canShowLineCrossings}
                                canShowSiteVideoWall={canShowSiteVideoWall}
                                onSiteVideoWall={() => setSiteWallMiniOpen(true)}
                                canViewProtocolNotes={
                                  !!(siteNotes || cameraNotes) &&
                                  !!selectedRow &&
                                  !pageLoading
                                }
                                onViewProtocolNotes={() => {
                                  setSiteId(selectedRow.customer_pk);
                                  setViewSiteProtocolDialogOpen(true);
                                }}
                              />
                            </div>
                          )}
                          </div>
                          <div
                            className="flex justify-center pl-[10px]"
                            style={{ paddingTop: videoPanelArrowTopPx }}
                            id="arrow_forward"
                          >
                            <OrangeTextButton
                              onClick={goToNextAlert}
                              text={
                                <ChevronRightIcon
                                  id="forwardButton"
                                  sx={ALERT_NAV_ARROW_ICON_SX}
                                />
                              }
                              disabled={forwardArrowDisabled}
                              sx={ALERT_NAV_ARROW_BUTTON_SX}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex grow flex-col pt-2">
                    <div className="flex w-full max-w-full grow flex-col">
                      <div className="flex w-full max-w-full grow flex-col">
                        {seeMoreURL && alertsToDisplay && selectedRow && (
                          <ErrorBoundary>
                            <AlertsIdentifierBox
                              selectedRow={selectedRow}
                              alertData={alertData}
                              setAlertData={setAlertData}
                              setSelectedRow={setSelectedRow}
                              selectdButtonsFilled={selectdButtonsFilled}
                              setSelectedButtonsFilled={setSelectedButtonsFilled}
                              navigate={navigate}
                              setPageLoading={setPageLoading}
                              setButtonClicked={setButtonClicked}
                              pageLoadedNumber={pageLoadedNumber}
                              setPageLoadedNumber={setPageLoadedNumber}
                              setFetchingFilteredData={setFetchingFilteredData}
                              setSeeMoreURL={setSeeMoreURL}
                              setMP4URL={setMP4URLWithMeta}
                              userFilters={userFilters}
                              setThumbnailVideos={setThumbnailVideos}
                              setDateOutsideRange={setDateOutsideRange}
                              setPage={setPage}
                              setVideoClipLoading={setVideoClipLoading}
                              setAlertsToDisplay={setAlertsToDisplay}
                              setSelectedAlertCameraInfo={setSelectedAlertCameraInfo}
                              setIgnoreZonesSelectedAlert={setIgnoreZonesSelectedAlert}
                              setAlertsIndexOrder={setAlertsIndexOrder}
                              setRefreshInterval={setRefreshInterval}
                              setCurrentName={setCurrentName}
                              setUserFilters={setUserFilters}
                              setRefreshingData={setRefreshingData}
                              refreshInterval={refreshInterval}
                              cameraOptions={cameraOptions}
                              siteOptions={siteOptions}
                              setRefreshingToken={setRefreshingToken}
                              refreshingToken={refreshingToken}
                              setAlertLabels={setAlertLabels}
                              setPeriodHours={setPeriodHours}
                              setQueryLimitHit={setQueryLimitHit}
                              setForwardArrowDisabled={setForwardArrowDisabled}
                              setBackArrowDisabled={setBackArrowDisabled}
                              setShowRefreshButton={setShowRefreshButton}
                              setRowsSortable={setRowsSortable}
                              setAlertsRefreshing={setAlertsRefreshing}
                              detectionType={detectionType}
                              outcomeFilterType={outcomeFilterType}
                              vlmFilterType={showVlmFilters ? vlmFilterType : "All"}
                              getCurrentSelectedRowKey={getCurrentSelectedRowKey}
                              setVideoProcessing={setVideoProcessing}
                              useAsyncGeneration={useAsyncGeneration}
                              syncSelectedRowRef={syncSelectedRowRef}
                              compact={tableDensity === "condensed"}
                            />
                          </ErrorBoundary>
                        )}
                        <div className="flex max-w-full grow flex-col overflow-x-auto pt-2">
                          {alertsToDisplay &&
                            seeMoreURL &&
                            !pageLoading &&
                            selectedRow && (
                              <CameraInfoTable
                                selectedRow={selectedRow}
                                setNoteText={setNoteText}
                                setNotesDialogOpen={setNotesDialogOpen}
                              />
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Paper>
            </div>
            )}
          </div>
            )}
          </div>
        </div>
      </div>
      <div className="justify-start">
        <NavBar
          navBarCollapsed={navBarCollapsed}
          setNavBarCollapsed={setNavBarCollapsed}
          props={{}}
        />
      </div>
    </div>
  );
};

export default React.memo(Alerts);
